import { ApiHeaderInfoOk } from 'src/types/v1-api-types';
import { ErrorBoundary, LinkStyled } from '@components/common';
import { LogoBjFull } from '@components/layout/LogoBj/LogoBjFull';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { getI18nLinkHref } from '@services/i18nPaths';
import { LogoBj } from './LogoBj';

interface LogoBjInterface {
  headerInfo?: ApiHeaderInfoOk;
  locale?: Locale;
  className?: string;
  iconColor?: string;
  textColor?: string;
  width?: string;
  height?: string;
  imageClassName?: string;
  showFullLogo?: boolean;
}

/**
 * @description Bestjobs logo component. Links straight to the Bestie coaching chat, regardless
 * of auth state or user role — it is Bestie's only entry point now that the mission links no
 * longer carry it.
 *
 * It targets `/bestie-chat` directly rather than the home root `/`: the root branches on auth
 * and sends anonymous visitors to the job list, so guests tapping the logo never reached
 * Bestie. `/bestie-chat` is absent from `auth-required-map.json`, so guests land there without
 * a login bounce. The root's own behaviour is deliberately left alone — it is where organic
 * search, ads and crawlers arrive.
 */
export const LogoBjLink = (props: LogoBjInterface) => {
  const {
    headerInfo,
    locale: propLocale,
    className = '',
    iconColor = 'fill-secondary',
    textColor = 'fill-ink',
    width = '100%',
    height = '100%',
    imageClassName = '',
    showFullLogo = false,
  } = props;


  // Resolve the active locale and the localized Bestie chat URL
  const { locale: routerLocale } = useExtendedRouter();
  const usedLocale = (propLocale || routerLocale);
  const bestieHref = getI18nLinkHref('/bestie-chat', usedLocale);


  const isCompany = !headerInfo || (headerInfo && headerInfo.role === 'COMPANY');
  const isUser = !headerInfo || (headerInfo && headerInfo.role === 'USER');

  // If the user is authenticated, we need to redirect him to the correct homepage page
  // *******************************************************************************
  if (headerInfo) {
    return (
      <ErrorBoundary
        fallback={<div className="py-1.5 text-xl font-bold">Bestjobs</div>}
      >
        <LinkStyled
          href="/bestie-chat"
          locale={usedLocale}
          aria-label="bestjobs"
          className={className}
          styling="none"
          onClick={(e) => {
            e.preventDefault();
            window.location.href = bestieHref;
          }}
        >
          <LogoBj />
        </LinkStyled>
      </ErrorBoundary>
    );
  }

  // If the user is not authenticated, we set the link to the home page or live page.
  // *******************************************************************************
  return (
    <ErrorBoundary
      fallback={<div className="py-1.5 text-xl font-bold">Bestjobs</div>}
    >
      <LinkStyled
        href="/bestie-chat"
        locale={usedLocale}
        aria-label="bestjobs"
        className={className}
        styling="none"
        onClick={(e) => {
          e.preventDefault();
          window.location.href = bestieHref;
        }}
      >
        {(isCompany || isUser) && !showFullLogo ? (
          <>
            <LogoBj />
          </>
        ) : (
          <LogoBjFull
            iconColor={iconColor}
            textColor={textColor}
            width={width}
            height={height}
            className={imageClassName}
          />
        )}
      </LinkStyled>
    </ErrorBoundary>
  );
};
